import { useAssistantState } from '@assistant-ui/react'
import * as HoverCard from '@radix-ui/react-hover-card'
import { forwardRef, useContext, useRef } from 'react'
import {
	extractSourcesFromMessage,
	useCitationNumbering,
} from '#app/components/assistant-ui/message-sources.js'
import { ThreadVariantContext } from '#app/components/assistant-ui/thread.js'
import { trackClickOut } from '#app/utils/plausible.client.js'
import {
	isInternetmedicin,
	sourceDisplayName,
	sourceLongName,
} from '#app/utils/source-classification.js'

/**
 * Scroll to the "Källor" footer section of the message containing the given element.
 */
function scrollToSourcesFrom(elementInMessage: HTMLElement | null) {
	if (!elementInMessage) return
	const messageRoot = elementInMessage.closest('[data-role="assistant"]')
	const sourcesFooter = messageRoot?.querySelector('[data-sources-footer]')
	if (sourcesFooter) {
		sourcesFooter.scrollIntoView({ behavior: 'smooth', block: 'start' })
	}
}

/**
 * Inner component that reads source data reactively.
 * Separated so the HoverCard shell is always mounted — even during streaming
 * before sources arrive — and the content updates when sources become available.
 *
 * Layout inspired by OpenEvidence: number + full title on top (bold, linked),
 * source name below in green, URL in tiny muted text, and a "Visa alla källor"
 * link to scroll to the footer.
 */
function CitationHoverContent({
	number,
	shown,
	triggerRef,
}: {
	number: number
	shown: number
	triggerRef: React.RefObject<HTMLSpanElement | null>
}) {
	const source = useAssistantState(({ message }) => {
		const sources = extractSourcesFromMessage(
			message.metadata as Record<string, unknown> | undefined,
		)
		if (!sources || number < 1 || number > sources.length) return null
		return sources[number - 1]
	})
	// Hook must be unconditionally called before any early return — react-hooks/rules-of-hooks.
	const variant = useContext(ThreadVariantContext)

	if (!source) {
		return (
			<div className="py-2 text-center text-xs text-muted-foreground">
				Laddar källa...
			</div>
		)
	}

	const sourceName = sourceDisplayName(source)
	const displayTitle = source.title || sourceName
	const isIM = isInternetmedicin(source.url)
	const accentColor = isIM ? 'text-green-700' : 'text-orange-700'
	const hoverColor = isIM ? 'group-hover:text-green-700' : 'group-hover:text-orange-700'
	const btnHover = isIM ? 'hover:text-green-900' : 'hover:text-orange-900'
	const location =
		variant === 'embed' ? 'assistent_embed' : 'assistent_start_page'
	const onClickOut = () =>
		trackClickOut({
			destinationUrl: source.url,
			destinationSource: sourceName,
			position: 'citation_hovercard',
			isInternal: isIM,
			// Originalnumret (plats i källramen), inte det visade: serien i
			// Plausible behåller samma betydelse som före IM-52.
			citationNumber: number,
			location,
		})

	return (
		<div className="space-y-2">
			{/* Title — full, never truncated */}
			<a
				href={source.url}
				target="_blank"
				rel="noopener noreferrer"
				onClick={onClickOut}
				className="group block"
			>
				<p className={`text-sm font-semibold leading-snug text-foreground ${hoverColor} transition-colors`}>
					{shown}. {displayTitle}
				</p>
			</a>

			{/* Source name — spelled out here, the chip keeps the short form */}
			<p className={`text-xs font-medium ${accentColor}`}>
				{sourceLongName(source)}
			</p>

			{/* URL — small, muted, wrapping, clickable */}
			<a
				href={source.url}
				target="_blank"
				rel="noopener noreferrer"
				onClick={onClickOut}
				className="block break-all text-[11px] leading-tight text-muted-foreground/70 hover:text-muted-foreground hover:underline transition-colors"
			>
				{source.url}
			</a>

			{/* Divider + scroll to all sources */}
			<div className="border-t pt-2">
				<button
					type="button"
					onClick={() => scrollToSourcesFrom(triggerRef.current)}
					className={`text-xs font-medium ${accentColor} ${btnHover} transition-colors cursor-pointer`}
				>
					Visa alla källor ↓
				</button>
			</div>
		</div>
	)
}

/**
 * Non-navigating badge label for the citation pill.
 * Serves as the HoverCard trigger — hover/click opens the card with details,
 * and the user can click the link INSIDE the card to navigate to the source.
 *
 * Uses forwardRef so Radix HoverCard.Trigger (asChild) can attach its ref
 * and event handlers to the DOM element.
 */
const CitationBadgeLabel = forwardRef<HTMLSpanElement, { number: number; shown: number } & React.HTMLAttributes<HTMLSpanElement>>(
	function CitationBadgeLabel({ number, shown, ...props }, ref) {
		const source = useAssistantState(({ message }) => {
			const sources = extractSourcesFromMessage(
				message.metadata as Record<string, unknown> | undefined,
			)
			if (!sources || number < 1 || number > sources.length) return null
			return sources[number - 1]
		})

		const sourceUrl = source?.url ?? null
		const sourceName = source ? sourceDisplayName(source) : null
		const isIM = sourceUrl ? isInternetmedicin(sourceUrl) : true

		const badgeClasses = `inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium align-super cursor-pointer transition-colors whitespace-nowrap ${isIM ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-orange-100 text-orange-800 hover:bg-orange-200'}`

		const label = `${shown}.${sourceName ? ` ${sourceName}` : ''}`

		return (
			<span ref={ref} className={badgeClasses} role="button" tabIndex={0} aria-label={`Källa ${shown}`} {...props}>
				{label}
			</span>
		)
	},
)

/**
 * Inline citation pill rendered as a labeled badge like "1. Internetmedicin".
 * Hovering (or clicking on mobile) opens a card with title, source name, URL,
 * and a link to navigate to the source. Inspired by OpenEvidence's source bubbles.
 *
 * The HoverCard is always mounted so hovering works during streaming —
 * content shows "Laddar källa..." until sources arrive, then updates reactively.
 */
export function CitationBubble({ number }: { number: number }) {
	const triggerRef = useRef<HTMLSpanElement>(null)
	// `number` är originalnumret ur texten och används för uppslaget mot
	// källan; etiketten visar numret i citatordning (IM-52). Ett citat utan
	// visat nummer ligger utanför källramen och får ingen bubbla.
	const numbering = useCitationNumbering()
	const shown = numbering ? numbering.get(number) : number
	if (shown === undefined) return null

	return (
		<HoverCard.Root openDelay={200} closeDelay={300}>
			<HoverCard.Trigger asChild>
				<CitationBadgeLabel ref={triggerRef} number={number} shown={shown} />
			</HoverCard.Trigger>
			<HoverCard.Portal>
				<HoverCard.Content
					side="top"
					align="center"
					sideOffset={5}
					className="z-50 w-80 rounded-lg border bg-popover p-4 shadow-lg animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
				>
					<CitationHoverContent
						number={number}
						shown={shown}
						triggerRef={triggerRef}
					/>
					<HoverCard.Arrow className="fill-popover" />
				</HoverCard.Content>
			</HoverCard.Portal>
		</HoverCard.Root>
	)
}
